import React, { useEffect } from 'react';
import { Card, Form, Button, Tabs, Input, message, Typography, Space } from 'antd';
import { SaveOutlined, EnvironmentOutlined, TranslationOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Paragraph } = Typography;
const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

const ContactInfoSettings: React.FC = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ['adminContactInfo'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/contact/info');
            return response.data.data;
        }
    });

    const contactInfo = pagedData?.[0];

    const updateMutation = useMutation({
        mutationFn: async (values: any) => {
            await apiClient.put('/admin/contact/info', values);
        },
        onSuccess: () => {
            message.success('Contact information updated successfully');
            queryClient.invalidateQueries({ queryKey: ['adminContactInfo'] });
        },
        onError: () => {
            message.error('Failed to update contact information');
        }
    });

    useEffect(() => {
        if (contactInfo) {
            const translations = appLanguages.map(lang => {
                const trans = contactInfo.translations?.find((t: any) => t.languageCode === lang) || {};
                return {
                    languageCode: lang,
                    contactTitle: trans.contactTitle || '',
                    address: trans.address || '',
                    email: trans.email || '',
                    phone: trans.phone || '',
                    openingHours: trans.openingHours || '',
                    mapUrl: trans.mapUrl || ''
                };
            });

            form.setFieldsValue({
                id: contactInfo.id,
                translations
            });
        }
    }, [contactInfo, form]);

    const onFinish = (values: any) => {
        updateMutation.mutate(values);
    };

    if (isLoading) return <div className="p-10 text-center">Loading...</div>;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <Title level={2} className="mb-0">Contact Information</Title>
                    <Paragraph type="secondary">Manage the contact details displayed on the public contact page.</Paragraph>
                </div>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    onClick={() => form.submit()}
                    loading={updateMutation.isPending}
                >
                    Save Changes
                </Button>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item name="id" hidden>
                    <Input />
                </Form.Item>

                <Card
                    title={<Space><EnvironmentOutlined /> <span>Localized Contact Details</span></Space>}
                    className="shadow-sm overflow-hidden"
                    styles={{ body: { padding: 0 } }}
                >
                    <div className="p-4 bg-blue-50 border-b border-blue-100 flex items-start gap-3">
                        <InfoCircleOutlined className="text-blue-500 mt-1" />
                        <Paragraph className="mb-0 text-blue-800 text-sm">
                            Configure address, email, phone, and opening hours for each supported language.
                            The <strong>Google Maps Embed URL</strong> can be found in Google Maps "Share" &rarr; "Embed a map".
                        </Paragraph>
                    </div>
                    <Tabs
                        type="line"
                        className="admin-settings-tabs px-6 py-2"
                        items={appLanguages.map((lang, index) => ({
                            key: lang,
                            label: (
                                <Space>
                                    <TranslationOutlined />
                                    {lang.toUpperCase()}
                                </Space>
                            ),
                            children: (
                                <div className="py-6 space-y-4">
                                    <Form.Item hidden name={['translations', index, 'languageCode']}>
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        label="Contact Section Title (e.g. Kontakt / İletişim)"
                                        name={['translations', index, 'contactTitle']}
                                        rules={[{ required: true, message: 'Title is required' }]}
                                    >
                                        <Input placeholder="Title displayed in Footer and Contact Page" />
                                    </Form.Item>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Form.Item
                                            label="Address"
                                            name={['translations', index, 'address']}
                                            rules={[{ required: true, message: 'Address is required' }]}
                                        >
                                            <Input.TextArea rows={3} placeholder="Full address including ZIP and City" />
                                        </Form.Item>

                                        <Form.Item
                                            label="Opening Hours"
                                            name={['translations', index, 'openingHours']}
                                        >
                                            <Input.TextArea rows={3} placeholder="e.g. Mon - Fri: 09:00 - 17:00" />
                                        </Form.Item>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Form.Item
                                            label="Email Address"
                                            name={['translations', index, 'email']}
                                            rules={[
                                                { required: true, message: 'Email is required' },
                                                { type: 'email', message: 'Please enter a valid email' }
                                            ]}
                                        >
                                            <Input placeholder="info@example.com" />
                                        </Form.Item>

                                        <Form.Item
                                            label="Phone Number"
                                            name={['translations', index, 'phone']}
                                        >
                                            <Input placeholder="+49 123 456789" />
                                        </Form.Item>
                                    </div>

                                    <Form.Item
                                        label="Google Maps Embed URL (iframe src)"
                                        name={['translations', index, 'mapUrl']}
                                        help="Paste the 'src' attribute value from the Google Maps iframe embed code."
                                    >
                                        <Input placeholder="https://www.google.com/maps/embed?pb=..." />
                                    </Form.Item>
                                </div>
                            )
                        }))}
                    />
                </Card>
            </Form>
        </div>
    );
};

export default ContactInfoSettings;
